// src/pages/portal/QRList.jsx
import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { motion } from 'framer-motion';
import { getUser } from '../../auth/Auth';
import { deleteRequest } from "../../routes/Routes";
import { loadWishes, loadTemplates } from "../../common/Common";
import TemplateGenerator from '../portal/TemplateGenerator';

import {
    Plus,
    Edit,
    Printer,
    Trash2,
    Clock,
    CheckCircle,
    AlertTriangle,
    Eye,
    ExternalLink,
    X
} from 'lucide-react';

const normalizeTemplate = (t = {}) => ({
    id: String(t.id ?? ''),
    name: t.name ?? '',
    type: t.type ?? 'birthday',
    description: t.description ?? '',
    backgroundGif: String(t.background_gif ?? t.backgroundGif ?? '').replace(/\\\//g, '/'),
    backgroundColor: t.background_color ?? t.backgroundColor ?? 'from-pink-100 via-purple-50 to-blue-100',
    textColor: t.text_color ?? t.textColor ?? 'text-gray-800',
    accentColor: t.accent_color ?? t.accentColor ?? 'from-pink-500 to-purple-500',
    createdAt: t.created_at ?? new Date().toISOString(),
});

/** Scales any child into a fixed viewport neatly (used ONLY in the modal) */
function ScaledPreview({ baseWidth = 800, baseHeight = 1200, height = 600, children }) {
    const containerRef = useRef(null);
    const [scale, setScale] = useState(1);
    const padding = 16;

    useLayoutEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const resize = () => {
            const { width, height: h } = el.getBoundingClientRect();
            const s = Math.min((width - padding * 2) / baseWidth, (h - padding * 2) / baseHeight);
            setScale(s > 0 ? s : 1);
        };
        resize();
        const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(resize) : null;
        ro?.observe(el);
        return () => ro?.disconnect();
    }, [baseWidth, baseHeight]);

    return (
        <div
            ref={containerRef}
            className="relative bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden"
            style={{ height }}
        >
            <div className="absolute inset-0 flex items-start justify-center p-4">
                <div
                    style={{
                        width: baseWidth,
                        height: baseHeight,
                        transform: `scale(0.65)`,
                        transformOrigin: 'top center', // keep preview from sliding down
                        borderRadius: '1rem',
                        boxShadow: '0 8px 30px rgba(0,0,0,0.08)',
                        overflow: 'hidden',
                        background: '#fff',
                    }}
                >
                    {children}
                </div>
            </div>
        </div>
    );
}

function QRList() {
    const [qrCodes, setQrCodes] = useState([]);
    const [deletingId, setDeletingId] = useState(null);
    const [user, setUser] = useState(null);
    const [loadingContent, setLoadingContent] = useState(true);

    // templates indexed by ID (unchanged, still loaded for your table view)
    const [templatesById, setTemplatesById] = useState({});
    const [loadingTemplates, setLoadingTemplates] = useState(true);

    // Quick preview modal state
    const [previewItem, setPreviewItem] = useState(null);

    // Close modal on ESC
    useEffect(() => {
        if (!previewItem) return;
        const onKey = (e) => e.key === 'Escape' && setPreviewItem(null);
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [previewItem]);

    // Load wishes + templates
    useEffect(() => {
        (async () => {
            try {
                const u = await getUser();
                setUser(u);
                if (!u?.token) {
                    setLoadingContent(false);
                    setLoadingTemplates(false);
                    return;
                }

                // Wishes
                const apiData = await loadWishes(u.token);
                const mapped = (apiData || []).map(w => ({
                    id: w.id,
                    type: w.type,
                    receiverName: w.receiver_name,
                    senderName: w.sender_name,
                    message: w.message,
                    templateId: String(w.page_template ?? w.template_id ?? ''),
                    createdAt: new Date(w.created_at),
                    expiresAt: new Date(w.expires_at),
                    qrToken: w.qr_token,
                    active: w.active
                }));
                setQrCodes(mapped);
                setLoadingContent(false);

                // Templates (for table meta only)
                setLoadingTemplates(true);
                const tRes = await loadTemplates(u.token);
                const list = Array.isArray(tRes) ? tRes : (tRes?.wishtemplates || []);
                const byId = {};
                list.forEach(raw => {
                    const t = normalizeTemplate(raw);
                    byId[t.id] = t;
                });
                setTemplatesById(byId);
            } catch (e) {
                setUser(null);
            } finally {
                setLoadingTemplates(false);
            }
        })();
    }, []);

    const deleteQR = async (id) => {
        if (!id) {
            toast.error('Invalid QR code id', { position: 'bottom-center' });
            return;
        }
        if (!user?.token) {
            toast.error('You are not authenticated', { position: 'bottom-center' });
            return;
        }

        const confirmed = window.confirm('Are you sure you want to delete this QR code?');
        if (!confirmed) return;

        try {
            setDeletingId(id);
            const res = await deleteRequest(`/api/wish/${id}`, user.token);

            if (res?.status === 200 && res.data && res.data.error === false) {
                setQrCodes(prev => prev.filter(qr => qr.id !== id));
                toast.success('QR code deleted successfully', { position: 'bottom-center', autoClose: 3000 });
            } else {
                throw new Error(res?.data?.message || 'Failed to delete QR code');
            }
        } catch (err) {
            toast.error(err.message || 'An error occurred while deleting', { position: 'bottom-center', autoClose: 3000 });
        } finally {
            setDeletingId(null);
        }
    };

    const getStatusInfo = (qr) => {
        const now = new Date();
        const diffMs = qr.expiresAt.getTime() - now.getTime();

        if (diffMs <= 0) {
            return { status: 'expired', label: 'Expired', icon: AlertTriangle, color: 'text-red-600', bgColor: 'bg-red-100' };
        }
        const totalMinutes = Math.floor(diffMs / (1000 * 60));
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;

        if (hours <= 6) {
            return { status: 'expiring', label: `${hours}h ${minutes}m left`, icon: Clock, color: 'text-orange-600', bgColor: 'bg-orange-100' };
        }
        return { status: 'active', label: `${hours}h ${minutes}m left`, icon: CheckCircle, color: 'text-green-600', bgColor: 'bg-green-100' };
    };

    const wishTypes = {
        birthday: '🎂 Birthday',
        anniversary: '❤️ Anniversary',
        congratulations: '🎉 Congratulations',
        retirement: '🏆 Retirement',
        graduation: '🎓 Graduation',
        wedding: '💒 Wedding',
        baby: '👶 Baby Shower',
        love: '💕 Love Note'
    };

    // For outside click on modal
    const overlayRef = useRef(null);
    const closeOnOverlay = (e) => {
        if (e.target === overlayRef.current) setPreviewItem(null);
    };

    return (
        <div className="p-4">
            <div className="container-fluid">
                {/* Header */}
                <div className="d-flex align-items-center justify-content-between mb-4">
                    <div>
                        <h1 className="fs-2 fw-bold text-dark mb-2">QR Code Management</h1>
                        <p className="text-muted">Manage your custom wish QR codes</p>
                    </div>

                    <Link
                        to="/portal/qrs/new"
                        className="btn gradient-secondary text-white px-4 py-3 rounded-3 fw-semibold d-flex align-items-center gap-2 shadow text-decoration-none"
                    >
                        <Plus size={20} />
                        Create New QR
                    </Link>
                </div>

                {/* Stats Cards */}
                <div className="row g-4 mb-4">
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="col-md-4">
                        <div className="card rounded-3 shadow-sm h-100">
                            <div className="card-body">
                                <div className="d-flex align-items-center gap-3">
                                    <div className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center" style={{ width: '3rem', height: '3rem' }}>
                                        <CheckCircle className="text-primary" size={24} />
                                    </div>
                                    <div>
                                        <p className="small text-muted mb-1">Total Active</p>
                                        <p className="fs-3 fw-bold text-dark mb-0">{qrCodes.length}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>

                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="col-md-4">
                        <div className="card rounded-3 shadow-sm h-100">
                            <div className="card-body">
                                <div className="d-flex align-items-center gap-3">
                                    <div className="bg-warning bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center" style={{ width: '3rem', height: '3rem' }}>
                                        <Clock className="text-warning" size={24} />
                                    </div>
                                    <div>
                                        <p className="small text-muted mb-1">Expiring Soon</p>
                                        <p className="fs-3 fw-bold text-dark mb-0">
                                            {qrCodes.filter(qr => {
                                                const hoursLeft = Math.floor((qr.expiresAt.getTime() - new Date().getTime()) / (1000 * 60 * 60));
                                                return hoursLeft <= 6 && hoursLeft > 0;
                                            }).length}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>

                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="col-md-4">
                        <div className="card rounded-3 shadow-sm h-100">
                            <div className="card-body">
                                <div className="d-flex align-items-center gap-3">
                                    <div className="bg-success bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center" style={{ width: '3rem', height: '3rem' }}>
                                        <Printer className="text-success" size={24} />
                                    </div>
                                    <div>
                                        <p className="small text-muted mb-1">Ready to Print</p>
                                        <p className="fs-3 fw-bold text-dark mb-0">
                                            {qrCodes.filter(qr => {
                                                const hoursLeft = Math.floor((qr.expiresAt.getTime() - new Date().getTime()) / (1000 * 60 * 60));
                                                return hoursLeft > 6;
                                            }).length}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>

                {/* QR Codes Table */}
                {qrCodes.length === 0 ? (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="card rounded-3 shadow-sm">
                        <div className="card-body text-center py-5">
                            <div className="bg-light rounded-circle d-flex align-items-center justify-content-center mx-auto mb-4" style={{ width: '4rem', height: '4rem' }}>
                                <Plus className="text-muted" size={32} />
                            </div>
                            <h3 className="fs-4 fw-semibold text-dark mb-2">No QR Codes Yet</h3>
                            <p className="text-muted mb-4">Create your first custom wish QR code to get started</p>
                            <Link to="/portal/qrs/new" className="btn gradient-secondary text-white px-4 py-3 rounded-3 fw-semibold d-inline-flex align-items-center gap-2 text-decoration-none">
                                <Plus size={20} />
                                Create New QR
                            </Link>
                        </div>
                    </motion.div>
                ) : (
                    <div className="card rounded-3 shadow-sm">
                        <div className="table-responsive">
                            <table className="table table-hover mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th className="fw-semibold text-dark">Wish Details</th>
                                        <th className="fw-semibold text-dark">Recipients</th>
                                        <th className="fw-semibold text-dark">Created</th>
                                        <th className="fw-semibold text-dark">Status</th>
                                        <th className="fw-semibold text-dark">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {qrCodes.map((qr, index) => {
                                        const statusInfo = getStatusInfo(qr);
                                        const StatusIcon = statusInfo.icon;

                                        return (
                                            <motion.tr key={qr.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }}>
                                                <td className="py-3">
                                                    <div>
                                                        <div className="fw-semibold text-dark">{({
                                                            birthday: '🎂 Birthday', anniversary: '❤️ Anniversary', congratulations: '🎉 Congratulations', retirement: '🏆 Retirement', graduation: '🎓 Graduation', wedding: '💒 Wedding', baby: '👶 Baby Shower', love: '💕 Love Note'
                                                        }[qr.type]) || qr.type}</div>
                                                        <div className="small text-muted">Template ID: {qr.templateId ?? '—'}</div>
                                                        <div className="small text-muted text-truncate" style={{ maxWidth: '200px' }}>{qr.message}</div>
                                                    </div>
                                                </td>
                                                <td className="py-3">
                                                    <div className="small">
                                                        <div className="fw-medium text-dark">To: {qr.receiverName}</div>
                                                        <div className="text-muted">From: {qr.senderName}</div>
                                                    </div>
                                                </td>
                                                <td className="py-3">
                                                    <div className="small text-muted">
                                                        {format(qr.createdAt, 'MMM dd, yyyy')}
                                                        <br />
                                                        {format(qr.createdAt, 'hh:mm a')}
                                                    </div>
                                                </td>
                                                <td className="py-3">
                                                    <span className={`badge rounded-pill d-inline-flex align-items-center gap-1 ${statusInfo.status === 'expired'
                                                        ? 'bg-danger'
                                                        : statusInfo.status === 'expiring'
                                                            ? 'bg-warning text-dark'
                                                            : 'bg-success'
                                                        }`}>
                                                        <StatusIcon size={14} />
                                                        {statusInfo.label}
                                                    </span>
                                                </td>
                                                <td className="py-3">
                                                    <div className="d-flex align-items-center gap-2">
                                                        {/* Quick Preview modal */}
                                                        <button
                                                            type="button"
                                                            onClick={() => setPreviewItem(qr)}
                                                            className="btn btn-sm btn-outline-primary rounded-3"
                                                            title="Quick Preview"
                                                        >
                                                            <Eye size={16} />
                                                        </button>

                                                        {/* Public page (opens new tab) */}
                                                        <a
                                                            href={`/wish/${qr.qrToken}`}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            className="btn btn-sm btn-outline-primary rounded-3"
                                                            title="Open Public Page"
                                                        >
                                                            <ExternalLink size={16} />
                                                        </a>

                                                        <Link to={`/portal/qrs/edit/${qr.id}`} className="btn btn-sm btn-outline-secondary rounded-3" title="Edit">
                                                            <Edit size={16} />
                                                        </Link>

                                                        <Link to={`/portal/print/${qr.id}`} className="btn btn-sm btn-outline-success rounded-3" title="Print QR Code">
                                                            <Printer size={16} />
                                                        </Link>

                                                        <button
                                                            onClick={() => deleteQR(qr.id)}
                                                            className="btn btn-sm btn-outline-danger rounded-3 d-inline-flex align-items-center gap-2"
                                                            title="Delete"
                                                            disabled={deletingId === qr.id}
                                                        >
                                                            {deletingId === qr.id && (
                                                                <span className="spinner-grow spinner-grow-sm" role="status" aria-hidden="true" />
                                                            )}
                                                            <Trash2 size={16} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </motion.tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>

            {/* Preview Modal — UPDATED ONLY THIS SECTION */}
            {previewItem && (
                <div ref={overlayRef} onClick={closeOnOverlay} className="fixed inset-0 z-[1050] flex min-h-screen items-center justify-center bg-black/60 backdrop-grayscale backdrop-blur-sm p-3 sm:p-6"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Quick Preview"
                >
                    <div
                        className="
              w-full
              max-w-[92rem] sm:max-w-4xl md:max-w-5xl lg:max-w-6xl
              bg-white rounded-2xl shadow-xl overflow-hidden
            "
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b">
                            <div className="min-w-0">
                                <h5 className="mb-0 font-semibold text-gray-900 truncate">Quick Preview</h5>
                                <p className="text-xs sm:text-sm text-gray-500 truncate">
                                    Token: {previewItem.qrToken || '—'}
                                </p>
                            </div>
                            <button
                                type="button"
                                className="inline-flex items-center rounded-lg p-2 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                                onClick={() => setPreviewItem(null)}
                                aria-label="Close"
                            >
                                <X className="w-5 h-5 text-gray-600" />
                            </button>
                        </div>

                        {/* Body (ScaledPreview + TemplateGenerator via qr_token) */}
                        <div className="p-3 sm:p-6">
                            <ScaledPreview>
                                <TemplateGenerator
                                    wishToken={previewItem.qrToken}   // ← load wish + wish-texts by token
                                    asModal={false}
                                />
                            </ScaledPreview>

                            <div className="mt-4 sm:mt-6 flex justify-center">
                                <a
                                    href={`/wish/${previewItem.qrToken}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="
                    inline-flex items-center gap-2
                    rounded-xl border border-gray-300 px-4 py-2
                    text-sm font-medium text-gray-700
                    hover:bg-gray-50
                    focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500
                  "
                                >
                                    <ExternalLink className="w-4 h-4" />
                                    Open public page
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default QRList;
